"use client";

import {
  useCallback,
  useRef,
  type KeyboardEvent,
  type ReactNode,
} from "react";

export type CcFaqPillTabItem = {
  id: string;
  label: ReactNode;
  /** Overrides the tab button's `id` (defaults to `cc-faq-pill-tab-${id}`). */
  tabId?: string;
};

type CcFaqPillTabsProps = {
  items: readonly CcFaqPillTabItem[];
  activeId: string;
  onSelect: (id: string) => void;
  /** Visible label element id (preferred when a field label is present). */
  ariaLabelledBy?: string;
  /** Fallback when no labelled-by target exists. */
  ariaLabel?: string;
  className?: string;
};

/**
 * FAQ category filter pills (`cc-faq__tab` / `cc-faq__tabs`) with roving tabindex
 * and arrow-key navigation — same interaction model as `CcFaqClient`.
 */
export function CcFaqPillTabs({
  items,
  activeId,
  onSelect,
  ariaLabelledBy,
  ariaLabel,
  className,
}: CcFaqPillTabsProps) {
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const tabCount = items.length;

  const handleTabKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
      let nextIndex: number | null = null;

      switch (event.key) {
        case "ArrowRight":
        case "ArrowDown":
          nextIndex = (index + 1) % tabCount;
          break;
        case "ArrowLeft":
        case "ArrowUp":
          nextIndex = (index - 1 + tabCount) % tabCount;
          break;
        case "Home":
          nextIndex = 0;
          break;
        case "End":
          nextIndex = tabCount - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      const nextItem = items[nextIndex];
      if (!nextItem) return;
      onSelect(nextItem.id);
      tabRefs.current[nextIndex]?.focus();
    },
    [items, onSelect, tabCount],
  );

  const tabsClassName = ["cc-faq__tabs", className].filter(Boolean).join(" ");

  return (
    <div
      className={tabsClassName}
      role="tablist"
      aria-labelledby={ariaLabelledBy}
      aria-label={ariaLabelledBy ? undefined : ariaLabel}
    >
      {items.map((item, index) => {
        const isActive = item.id === activeId;
        const tabId = item.tabId ?? `cc-faq-pill-tab-${item.id}`;

        return (
          <button
            key={item.id}
            ref={(node) => {
              tabRefs.current[index] = node;
            }}
            type="button"
            role="tab"
            id={tabId}
            className={`cc-faq__tab${isActive ? " is-active" : ""}`}
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onSelect(item.id)}
            onKeyDown={(event) => handleTabKeyDown(event, index)}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
